import React, { Component } from 'react';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { injectIntl } from 'react-intl';
import Typography from '@material-ui/core/Typography';

import { FormEdit } from 'Components/FormEdit';
import DateInput from 'Components/inputs/Date';
import { getLinkId } from 'Services/tools';
import TeamInput from '../../teams/Input';
import { CompetitionInput } from '../../competitions/CompetitionInput';
import { fetchCompetitionTournaments } from '../../tournaments/actions';
import TournamentField from './TournamentField';
import TournamentNameInput from './TournamentNameInput';

// tournament_award.tournament_name is a VARCHAR(9) NOT NULL column.
const TOURNAMENT_NAME_MAX = 9;

const required = value =>
  value || value === 0 ? undefined : 'error_required';
const maxTournamentName = value =>
  value && String(value).length > TOURNAMENT_NAME_MAX
    ? 'tournamentAward_nameTooLong'
    : undefined;

// Stable validator arrays (redux-form re-registers a field when its validate ref changes).
const VALIDATE_REQUIRED = [required];
const VALIDATE_TOURNAMENT_NAME = [required, maxTournamentName];

/**
 * Create/edit form for a tournament award.
 *
 * - On edit the competition is fixed (read-only display): an award never moves to
 *   another competition.
 * - The tournament picker is always restricted to the selected competition's
 *   tournaments, so the tournament fields only appear once a competition is set.
 * - All relations are submitted as IRIs (references), never as nested objects, so we
 *   never write into Competition / Tournament / Team by nesting.
 */
class Form extends Component {
  state = { tournaments: [] };

  componentDidMount() {
    if (this.props.competitionValue) {
      this.loadTournaments(this.props.competitionValue);
    }
  }

  componentDidUpdate(prevProps) {
    const { competitionValue } = this.props;
    if (prevProps.competitionValue === competitionValue) {
      return;
    }
    if (competitionValue) {
      this.loadTournaments(competitionValue);
    } else {
      this.setState({ tournaments: [] });
    }
  }

  loadTournaments(competition) {
    const id = getLinkId(competition) || competition;
    // restFootData transforms hydra collections into { collection, totalItems, pagination }
    this.props.dispatch(fetchCompetitionTournaments(id)).then(({ payload }) => {
      this.setState({ tournaments: (payload && payload.collection) || [] });
    });
  }

  buildConfig() {
    const { competitionFixed, competitionOption, competitionValue } = this.props;
    const { tournaments } = this.state;

    const fields = [];

    // Competition is fixed (read-only) when it is already known — on edit, or when
    // creating from the competition page. It is an editable, required picker only when
    // the origin doesn't provide one (creating from the team page).
    if (!competitionFixed) {
      fields.push({
        name: 'competition',
        component: CompetitionInput,
        selectedCompetitionOption: competitionOption,
        validate: VALIDATE_REQUIRED,
        size: 12,
      });
    }

    // Tournament-related fields only make sense once a competition is selected:
    // the tournament list is restricted to that competition's tournaments.
    // The tournament itself is optional (manual entry), but its name + dates are required.
    if (competitionValue) {
      fields.push({ name: 'tournament', component: TournamentField, tournaments, size: 12 });
      fields.push({
        name: 'tournamentName',
        component: TournamentNameInput,
        validate: VALIDATE_TOURNAMENT_NAME,
        size: 12,
      });
      fields.push({
        name: 'tournamentStartDate',
        component: DateInput,
        validate: VALIDATE_REQUIRED,
        size: 6,
      });
      fields.push({
        name: 'tournamentEndDate',
        component: DateInput,
        validate: VALIDATE_REQUIRED,
        size: 6,
      });
    }

    fields.push({ name: 'winnerTeam', component: TeamInput, size: 6 });
    fields.push({ name: 'runnerUpTeam', component: TeamInput, size: 6 });

    return {
      fields: ['tournamentName', 'tournamentStartDate', 'tournamentEndDate'],
      objectFields: ['competition', 'tournament', 'winnerTeam', 'runnerUpTeam'],
      disposition: [{ fields }],
    };
  }

  render() {
    const {
      data,
      onSubmit,
      onDelete,
      competitionFixed,
      competitionOption,
      form = 'tournamentAward_create',
      intl: { messages: m },
    } = this.props;

    return (
      <React.Fragment>
        {competitionFixed && competitionOption && (
          <div style={{ marginBottom: 16 }}>
            <Typography variant="caption" color="textSecondary">
              {m.field_competition}
            </Typography>
            <Typography variant="body1">{competitionOption.label}</Typography>
          </div>
        )}
        <FormEdit
          form={form}
          entity="tournamentAward"
          onSubmit={onSubmit}
          onDelete={onDelete}
          data={data}
          config={this.buildConfig()}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const form = ownProps.form || 'tournamentAward_create';

  return {
    competitionValue: formValueSelector(form)(state, 'competition'),
  };
};

export default connect(mapStateToProps)(injectIntl(Form));
